import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useCallback } from 'react';

const THEME_COOKIE = 'znajdki_theme';
const FONT_SIZE_COOKIE = 'znajdki_font_size';

type Theme = 'light' | 'dark' | 'high-contrast';

export function useAccessibilitySettings() {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = Cookies.get(THEME_COOKIE);
    return (savedTheme as Theme) || 'light';
  });
  
  const [fontSize, setFontSize] = useState<number>(() => {
    const savedFontSize = Cookies.get(FONT_SIZE_COOKIE);
    if (savedFontSize) return parseInt(savedFontSize, 10);
    if (typeof window !== 'undefined') {
      return 100;
    }
    return 100;
  });

  // Apply font size to root element
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}%`;
  }, [fontSize]);

  useEffect(() => {
    document.documentElement.classList.remove('dark', 'high-contrast');
    
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'high-contrast') {
      document.documentElement.classList.add('high-contrast');
    }
    
    // Save theme to cookie with 365-day expiration and path set to root
    Cookies.set(THEME_COOKIE, theme, { 
      expires: 365, 
      sameSite: 'strict',
      path: '/' 
    });
  }, [theme]);

  useEffect(() => {
    // Save font size to cookie with path set to root
    Cookies.set(FONT_SIZE_COOKIE, fontSize.toString(), { 
      expires: 365, 
      sameSite: 'strict',
      path: '/' 
    });
  }, [fontSize]);

  const toggleTheme = useCallback((newTheme: Theme) => {
    setTheme(newTheme);
  }, []);

  const adjustFontSize = useCallback((delta: number) => {
    setFontSize((prev) => {
      const newSize = Math.min(Math.max(prev + delta, 85), 200);
      return newSize;
    });
  }, []);

  const resetFontSize = useCallback(() => {
    setFontSize(100);
  }, []);

  return {
    theme,
    fontSize,
    toggleTheme,
    adjustFontSize,
    resetFontSize,
    isDark: theme === 'dark',
    isHighContrast: theme === 'high-contrast'
  };
}